import { cleanPhoneNumber, isInvalidAllZerosPhone } from '@/utils/phoneNumber.utils';
import { sanitizeErrorMessage } from '@/utils/sanitizeError';

const EMAIL_VALIDATION_REGEX =
  /^[a-z0-9](?:[a-z0-9._%+-]{0,62}[a-z0-9])?@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;

export const INDIA_COUNTRY_CODE = '+91';
export const INDIA_PHONE_LENGTH = 10;
export const INTERNATIONAL_PHONE_MIN_LENGTH = 4;
export const INTERNATIONAL_PHONE_MAX_LENGTH = 14;

const MAGIC_FILL_INDIA_MOBILE_BASE = 7_000_000_000;
const MAGIC_FILL_INDIA_MOBILE_SPAN = 1_000_000_000;
const MAGIC_FILL_EMAIL_DOMAIN = 'example.com';

/** Invite creates the profile asynchronously, so the follow-up save retries before giving up. */
export const PROFILE_SAVE_ATTEMPTS = 3;

/** 250ms, then 500ms. Long enough for the new profile row to become readable. */
export const PROFILE_SAVE_RETRY_BASE_DELAY_MS = 250;

export type InviteEmployeeFieldErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  programId?: string;
};

const INVITE_FIELD_BY_API_KEY: Record<string, keyof InviteEmployeeFieldErrors> = {
  email: 'email',
  first_name: 'firstName',
  last_name: 'lastName',
  phone_number: 'phoneNumber',
  phone: 'phoneNumber',
  date_of_birth: 'dateOfBirth',
  program: 'programId',
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const withProfileSaveRetry = async <T>(
  task: () => Promise<T>,
  attempts = PROFILE_SAVE_ATTEMPTS,
  sleep: (ms: number) => Promise<void> = wait
) => {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;
      if (attempt < attempts - 1) {
        await sleep(PROFILE_SAVE_RETRY_BASE_DELAY_MS * (attempt + 1));
      }
    }
  }
  throw lastError;
};

const firstFieldMessage = (value: unknown) => {
  const raw = Array.isArray(value) ? value.find((entry) => typeof entry === 'string') : value;
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  const sanitized = sanitizeErrorMessage(raw);
  return sanitized === 'An error occurred. Please try again.'
    ? 'Please check this field and try again.'
    : sanitized;
};

/** Map a DRF validation payload onto invite form fields. Unknown keys are ignored. */
export const parseInviteFieldErrors = (error: unknown): InviteEmployeeFieldErrors => {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};

  return Object.entries(data as Record<string, unknown>).reduce<InviteEmployeeFieldErrors>(
    (errors, [apiField, value]) => {
      const formField = INVITE_FIELD_BY_API_KEY[apiField];
      const message = formField ? firstFieldMessage(value) : undefined;
      if (formField && message) {
        errors[formField] = message;
      }
      return errors;
    },
    {}
  );
};

const slugifyName = (value: string, fallback: string) => {
  const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '');
  return slug || fallback;
};

export const isValidEmail = (value: string) => EMAIL_VALIDATION_REGEX.test(value.trim());

export const getSafeErrorMessage = (error: unknown) => {
  const axiosError = error as {
    response?: { status?: number };
    message?: string;
  };
  const status = axiosError.response?.status;
  if (status === 400) return 'Please check the form details and try again.';
  if (status === 401 || status === 403) return 'Your session has expired. Please sign in again.';
  if (status === 409) return 'An employee with this email already exists.';
  if (status && status >= 500) return 'Server error while creating employee. Please try again.';
  if (axiosError.message === 'Network Error') return 'Network error. Please check your connection.';
  return 'Unable to create employee right now. Please try again.';
};

export const getPhoneMaxLength = (countryCode: string) =>
  countryCode === INDIA_COUNTRY_CODE ? INDIA_PHONE_LENGTH : INTERNATIONAL_PHONE_MAX_LENGTH;

export const applyInvitePhoneCountryChange = (phoneNumber: string, countryCode: string) =>
  cleanPhoneNumber(phoneNumber, countryCode).slice(0, getPhoneMaxLength(countryCode));

export const phoneValidationMessage = (phoneNumber: string, countryCode: string) => {
  const digits = phoneNumber.replace(/\D/g, '');
  const maxLength = getPhoneMaxLength(countryCode);
  if (!digits) return 'Phone number is required';
  if (isInvalidAllZerosPhone(digits)) return 'Phone number cannot be all zeros';
  if (digits.length > maxLength) {
    return countryCode === INDIA_COUNTRY_CODE
      ? 'Enter exactly 10 digits for India (+91)'
      : `Enter at most ${maxLength} digits for the selected country code`;
  }
  if (countryCode === INDIA_COUNTRY_CODE) {
    return digits.length === INDIA_PHONE_LENGTH
      ? undefined
      : 'Enter exactly 10 digits for India (+91)';
  }
  return digits.length >= INTERNATIONAL_PHONE_MIN_LENGTH
    ? undefined
    : 'Enter between 4 and 14 digits for the selected country code';
};

/** Magic Fill sample data: a 10-digit Indian mobile for +91. */
export const generateMagicFillPhone = () => {
  const offset = Math.floor(Math.random() * MAGIC_FILL_INDIA_MOBILE_SPAN);
  return String(MAGIC_FILL_INDIA_MOBILE_BASE + offset);
};

/** Magic Fill sample data. example.com is reserved and cannot create a real mailbox. */
export const generateMagicFillEmail = (firstName: string, lastName: string) => {
  const firstSlug = slugifyName(firstName, 'member');
  const lastSlug = slugifyName(lastName, 'corporate');
  const randomSuffix = Math.random().toString(36).slice(2, 6);
  return `${firstSlug}.${lastSlug}.${randomSuffix}@${MAGIC_FILL_EMAIL_DOMAIN}`;
};
